import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts'
import { 
  BarChart3,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react'

interface ShareClass {
  equity_class_id: string
  class_name: string
  outstanding_shares: number
  percentage_of_total: number
}

interface ShareUsageChartProps {
  authorizedShares: number
  commonSharesAuthorized?: number
  preferredSharesAuthorized?: number
  optionPoolShares?: number
  shareClasses: ShareClass[]
  availableOptionPoolShares?: number
}

const COLORS = {
  common: '#3B82F6',      // Blue
  preferred: '#8B5CF6',   // Purple
  options: '#10B981',     // Green
  available: '#E5E7EB',   // Gray
  warrants: '#F59E0B',    // Amber
  other: '#6B7280'        // Gray
}

export default function ShareUsageChart({
  authorizedShares,
  commonSharesAuthorized = 0,
  preferredSharesAuthorized = 0,
  optionPoolShares = 0,
  shareClasses,
  availableOptionPoolShares = 0
}: ShareUsageChartProps) {
  // Calculate total issued shares
  const totalIssuedShares = shareClasses.reduce((sum, sc) => sum + sc.outstanding_shares, 0)
  const totalAvailableShares = authorizedShares - totalIssuedShares
  const utilizationPercentage = (totalIssuedShares / authorizedShares) * 100

  // Prepare data for pie chart
  const pieData = shareClasses
    .filter(sc => sc.outstanding_shares > 0)
    .map(sc => ({
      name: sc.class_name,
      value: sc.outstanding_shares,
      percentage: sc.percentage_of_total
    }))

  // Add available shares to pie chart
  if (totalAvailableShares > 0) {
    pieData.push({
      name: 'Available',
      value: totalAvailableShares,
      percentage: (totalAvailableShares / authorizedShares) * 100
    })
  }

  // Prepare data for authorization breakdown
  const authorizationData = [
    {
      name: 'Common',
      authorized: commonSharesAuthorized,
      issued: shareClasses.find(sc => sc.class_name === 'Common')?.outstanding_shares || 0
    },
    {
      name: 'Preferred',
      authorized: preferredSharesAuthorized,
      issued: shareClasses.filter(sc => sc.class_name.includes('Preferred'))
        .reduce((sum, sc) => sum + sc.outstanding_shares, 0)
    },
    {
      name: 'Options',
      authorized: optionPoolShares,
      issued: shareClasses.find(sc => sc.class_name === 'Options')?.outstanding_shares || 0
    }
  ].filter(item => item.authorized > 0)

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num))
  }

  const getColor = (className: string) => {
    const lowerName = className.toLowerCase()
    if (lowerName === 'common') return COLORS.common
    if (lowerName.includes('preferred')) return COLORS.preferred
    if (lowerName === 'options') return COLORS.options
    if (lowerName === 'available') return COLORS.available
    if (lowerName === 'warrants') return COLORS.warrants
    return COLORS.other
  }

  return (
    <div className="space-y-4">
      {/* Overall Usage Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Share Authorization Usage</CardTitle>
            <Badge variant={utilizationPercentage > 90 ? "destructive" : utilizationPercentage > 75 ? "secondary" : "default"}>
              {utilizationPercentage.toFixed(1)}% Utilized
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{formatNumber(totalIssuedShares)} issued</span>
                <span>{formatNumber(totalAvailableShares)} available</span>
              </div>
              <Progress value={utilizationPercentage} className="h-3" />
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-500">0</span>
                <span className="font-medium">{formatNumber(authorizedShares)} authorized</span>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{formatNumber(totalIssuedShares)}</p>
                <p className="text-sm text-gray-500">Total Issued</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{formatNumber(totalAvailableShares)}</p>
                <p className="text-sm text-gray-500">Available</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{formatNumber(authorizedShares)}</p>
                <p className="text-sm text-gray-500">Authorized</p>
              </div>
            </div>

            {/* Warnings */}
            {utilizationPercentage > 90 && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">High Utilization Warning</p>
                  <p className="text-sm text-red-700">
                    Over 90% of authorized shares are issued. Consider increasing authorization.
                  </p>
                </div>
              </div>
            )}

            {availableOptionPoolShares < (optionPoolShares * 0.1) && optionPoolShares > 0 && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Low Option Pool</p>
                  <p className="text-sm text-yellow-700">
                    Less than 10% of option pool remains. Consider refreshing the pool.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Share Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ value, percentage }) => `${formatNumber(value)} (${percentage.toFixed(1)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value: number) => formatNumber(value)}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => value}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Authorization vs Issued */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Authorization vs Issuance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={authorizationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => formatNumber(value)} />
                <RechartsTooltip 
                  formatter={(value: number) => formatNumber(value)}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="authorized" fill="#E5E7EB" name="Authorized" />
                <Bar dataKey="issued" fill="#3B82F6" name="Issued" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Share Class Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {shareClasses.map((shareClass) => {
              const classAuthorized = shareClass.class_name === 'Common' ? commonSharesAuthorized :
                                    shareClass.class_name.includes('Preferred') ? preferredSharesAuthorized :
                                    shareClass.class_name === 'Options' ? optionPoolShares : 0
              const classUtilization = classAuthorized > 0 ? (shareClass.outstanding_shares / classAuthorized) * 100 : 0

              return (
                <div key={shareClass.equity_class_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: getColor(shareClass.class_name) }}
                    />
                    <div>
                      <p className="font-medium">{shareClass.class_name}</p>
                      {classAuthorized > 0 && (
                        <p className="text-sm text-gray-500">
                          {formatNumber(shareClass.outstanding_shares)} of {formatNumber(classAuthorized)} authorized
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{shareClass.percentage_of_total.toFixed(2)}%</p>
                    {classAuthorized > 0 && (
                      <p className="text-sm text-gray-500">{classUtilization.toFixed(1)}% utilized</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}